import { useQuery } from "@tanstack/react-query";
import { getCoverage, getDatasetBuilderProgress } from "./api";
import { stageRecord } from "./stages";
import type { DatasetBuilderProgress } from "./types";

type Owner = { sessionId?: string | null; datasetId?: string | null };

/** The stages of a Comprehensive Dataset Builder run. A chat without stages has none. */
export const useDatasetBuilderProgress = ({ sessionId, datasetId }: Owner) =>
  useQuery({
    queryKey: ["dataset-builder-progress", sessionId ?? null, datasetId ?? null],
    queryFn: () =>
      getDatasetBuilderProgress(
        datasetId ? { datasetId } : { sessionId: sessionId! },
      ),
    enabled: Boolean(sessionId || datasetId),
    refetchInterval: (query) => {
      const stages = query.state.data?.stages ?? [];
      return stageRecord(query.state.data, "coverage")?.data.complete
        ? false
        : stages.length || !datasetId
          ? 5_000
          : 15_000;
    },
  });

/** The coverage of the master list, while the papers are read. */
export const useMasterListCoverage = (
  datasetId: string | null,
  progress: DatasetBuilderProgress | undefined,
) => {
  const resultId = stageRecord(progress, "master_list")?.data.result_id ?? null;
  const finished = stageRecord(progress, "coverage")?.status === "done";
  const started = Boolean(stageRecord(progress, "reading"));
  return useQuery({
    queryKey: ["dataset-builder-coverage", datasetId, resultId],
    queryFn: () => getCoverage(datasetId!, resultId!),
    enabled: Boolean(datasetId && resultId && started),
    refetchInterval: finished ? false : 30_000,
  });
};
